import { FormGroup } from "@angular/forms";
import { Observable } from "rxjs";
import { Asociacion, AsociacionesService, Asociado, AsociadosService, Cargo, CargosService, Ejercicio, EjerciciosService, Historico, HistoricoService, PostRequestAsociado, TipoAsociacion, TipoAsociacionesService } from "../../../api";
import { EncoderService } from "../../services/encoder.service";
import { AlertService } from "../alert/alert.service";
import { ExtraFieldsForm, FieldsForm } from "./data-fields";
import { FormButton } from "./formButton.model";
import { FormButtonType } from "./formButtonType.enum";
import { BodyRequest, FunctionsAvailable, ItemType, ServiceRequest } from "./request.model";

export const FORM_BUTTONS: Record<FormButtonType, FormButton> = {
    [FormButtonType.Aceptar]: { class: 'btn btn-primary me-auto px-5', text: FormButtonType.Aceptar, type: 'submit', action: () => undefined },
    [FormButtonType.Cancelar]: {
        class: 'btn btn-outline-secondary px-4', text: FormButtonType.Cancelar, type: 'button', action: () => {
            window.location.reload();
        }
    },
    [FormButtonType.Borrar]: {
        class: 'btn btn-outline-secondary px-4', text: FormButtonType.Borrar, type: 'button', action: (formulario: FormGroup) => {
            formulario.reset();
        }
    },
    [FormButtonType.Eliminar]: { class: 'btn btn-danger px-4', text: FormButtonType.Eliminar, type: 'button', action: () => undefined },
    [FormButtonType.Crear]: {
        class: 'btn btn-primary me-auto px-5', text: FormButtonType.Crear, type: 'button', action: (formulario: FormGroup, functionSelected: FunctionsAvailable, service: ServiceRequest, type: ItemType, alertService: AlertService) => {
            if (formulario.valid && formulario.touched) {
                //@ts-ignore
                callApi(functionSelected, service, prepareBody(formulario, type, functionSelected), alertService);
            }
        }
    },
    [FormButtonType.Editar]: {
        class: 'btn btn-primary me-auto px-5', text: FormButtonType.Editar, type: 'button', action: (formulario: FormGroup, functionSelected: FunctionsAvailable, service: ServiceRequest, type: ItemType, alertService: AlertService) => {
            if (formulario.valid && formulario.touched) {
                //@ts-ignore
                callApi(functionSelected, service, prepareBody(formulario, type, functionSelected), alertService);
            }
        }
    }
};

function createBody(formulario: FormGroup, type: ItemType, functionSelected: FunctionsAvailable) {
    const encodableFields = ['password', 'confirmPassword'];
    const nullableFields = ['password', 'confirmPassword'];
    let body: { [key: string]: any } = {};
    for (let key of Object.values(FieldsForm[type])) {
        let value = formulario.get(key.name)?.value;
        if (encodableFields.includes(key.name)) {
            let encoderService = new EncoderService();
            value = encoderService.encrypt(value);
        }
        if (key.name === 'id' && functionSelected.includes('Post')) {
            value = 0;
        }
        if (functionSelected.includes('Put') && nullableFields.includes(key.name)) {
            value = null;
        } else if (key.name === 'obligatorio') {
            value = Boolean(value);
        } else if (key.type === 'number' || key.type === 'checkbox' || (key.type === 'select' && !key.options)) {
          value = Number(value);
        }
        if (key.name !== 'confirmPassword') {
            body[key.name] = value;
        }
    }

    return body;

}

function fillBody(formulario: FormGroup, type: ItemType, functionSelected: FunctionsAvailable) {
    let body = createBody(formulario, type, functionSelected);
    if (type === ItemType.POSTREQUESTASOCIADO) {
        let newBody: PostRequestAsociado = {
            //@ts-ignore
            asociado: body,
        }
        for (let field of ExtraFieldsForm.PostRequestAsociado) {
            newBody[field.name] = formulario.get(field.name)?.value;
        }
        body = newBody;
    }
    return body;
}

function checkPasswords(formulario: FormGroup) {
    if (formulario.get('password')?.value === '' && formulario.get('confirmPassword')?.value !== '') {
        throw new Error('La contraseña no debe estar vacía.');
    }
    if (formulario.get('password')?.value !== formulario.get('confirmPassword')?.value) {
        throw new Error('Las contraseñas no coinciden.');
    }
}

function prepareBody(formulario: FormGroup, type: ItemType, functionSelected: FunctionsAvailable) {
    let body: { [key: string]: any } = {};
    if (formulario.contains('password') && !functionSelected.includes('Put')) {
        try {
            checkPasswords(formulario);
            body = fillBody(formulario, type, functionSelected);
        } catch (error) {
            if (error instanceof Error) {
                body['error'] = error.message;
            }
        }
    } else {
        body = fillBody(formulario, type, functionSelected);
    }
    return body;
}

type ServiceFunction = (service: ServiceRequest, body: BodyRequest) => Observable<any>;

type ServiceMap = {
    [key: string]: ServiceFunction;
    asociacionesPost: ServiceFunction;
    asociadosPost: ServiceFunction;
    ejerciciosPost: ServiceFunction;
    cargosPost: ServiceFunction;
    tipoasociacionesPost: ServiceFunction;
    historicoPost: ServiceFunction;

    asociacionesPut: ServiceFunction;
    asociadosPut: ServiceFunction;
    cargosPut: ServiceFunction;
    ejerciciosPut: ServiceFunction;
    tipoasociacionesPut: ServiceFunction;
    // historicoPut: ServiceFunction;
};

/** Mensaje de éxito de una acción de formulario. El alta del histórico indica qué movimiento se ha registrado (Alta, Cambio o Reingreso). */
export function mensajeExitoAccion(type: string, res: any): string {
    if (type === 'historicoPost' && res?.movimiento) {
        const { tipo, reingreso } = res.movimiento;
        if (tipo === 'alta') return reingreso ? 'Reingreso registrado: nueva alta tras la baja anterior.' : 'Alta registrada en el histórico.';
        return reingreso ? 'Cambio de cargo registrado: se ha recuperado un cargo que constaba como baja.' : 'Cambio de cargo registrado en el histórico.';
    }
    return `${type} creado con éxito!`;
}

export function callApi(type: string, service: ServiceRequest, body: BodyRequest, alertService: AlertService) {
    const typeToFunctionMap: ServiceMap = {
        'asociacionesPost': (service: ServiceRequest, body: BodyRequest) => (service as AsociacionesService).asociacionesPost(body as Asociacion),
        'asociadosPost': (service: ServiceRequest, body: BodyRequest) => (service as AsociadosService).asociadosPost(body as PostRequestAsociado),
        'ejerciciosPost': (service: ServiceRequest, body: BodyRequest) => (service as EjerciciosService).ejerciciosPost(body as Ejercicio),
        'cargosPost': (service: ServiceRequest, body: BodyRequest) => (service as CargosService).cargosPost(body as Cargo),
        'tipoasociacionesPost': (service: ServiceRequest, body: BodyRequest) => (service as TipoAsociacionesService).tipoasociacionesPost(body as TipoAsociacion),
        'historicoPost': (service: ServiceRequest, body: BodyRequest) => (service as HistoricoService).historicoPost(body as Historico),

        'asociacionesPut': (service: ServiceRequest, body: BodyRequest) => (service as AsociacionesService).asociacionesPut((body as Asociacion), (body as Asociacion).id!),
        'asociadosPut': (service: ServiceRequest, body: BodyRequest) => (service as AsociadosService).asociadosPut((body as Asociado), (body as Asociado).id!),
        'cargosPut': (service: ServiceRequest, body: BodyRequest) => (service as CargosService).cargosPut((body as Cargo), (body as Cargo).id!),
        'ejerciciosPut': (service: ServiceRequest, body: BodyRequest) => (service as EjerciciosService).ejerciciosPut((body as Ejercicio), (body as Ejercicio).id!),
        'tipoasociacionesPut': (service: ServiceRequest, body: BodyRequest) => (service as TipoAsociacionesService).tipoasociacionesPut((body as TipoAsociacion), (body as TipoAsociacion).id!),
        // 'historicoPut': (service: ServiceRequest, body: BodyRequest) => (service as HistoricoService).historicoPut((body as Historico), (body as Historico).idAsociacion!, (body as Historico).idAsociado!, (body as Historico).idCargo!, (body as Historico).ejercicio!),
    }
    if (typeToFunctionMap[type]) {

        if (!body['error']) {
            typeToFunctionMap[type](service, body).subscribe({
                next: (res: any) => {

                    if (res.status.status === 200) {
                        alertService.success(mensajeExitoAccion(type, res), 5000);
                        window.location.reload();
                    } else {
                        alertService.danger(`Ha surgido un problema`, 5000);
                    }
                },
                error: (error: any) => {
                    // El usuario debe ver siempre por qué no se ha podido guardar (mensaje seguro del servidor).
                    const detalle = error?.error?.status?.message || error?.error?.message;
                    alertService.danger(detalle || 'No se ha podido completar la operación.', 5000);
                }
            });
        } else {
            alertService.danger(body['error'], 5000)
        }

    } else {
        console.error(`Tipo no reconocido: ${type}`);
    }
}
